import React, { useEffect, useState } from "react";
import { Button, DatePicker, Input, InputNumber, Modal, Radio, Upload, message } from "antd";
import { PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import EstimateProcedures from "./EstimateProcedures";
import { createSubmissionResult, updateSubmissionResult } from "./estimateClaimsApi";
import { requestTypeMeta } from "../../../InsuranceRequests/requestTypes";
import {
  FTC_OUTCOMES_BY_TYPE, isAnswer, isFollowUp, isOfficeOutcome, outcomeChipStyle, outcomeLabel,
} from "../../../InsuranceRequests/outcomes";
import { describeApiError } from "../../lib/apiError";
import "./estimateClaims.css";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";

function toNumber(v) {
  return v === null || v === undefined || v === "" ? null : Number(v);
}

function toField(v) {
  return v === null || v === undefined ? "" : String(v);
}

/**
 * Record (or edit) what the carrier said about ONE requested submission.
 *
 * The owner's format, in order: "<carrier> contacted on <date>" (the title),
 * the procedures assessed (pre-filled from the office's request, editable),
 * claim amount, eligible amount, payable amount, paid amount
 * (claim follow-ups only), a note, and the carrier's response as an attachment.
 *
 * "What came back?" is the entry's outcome — the carrier's answer (Covered /
 * Partially covered / Not covered for an estimate, Paid for a claim), a check
 * for later (Payment being processed, Queued for follow-up — with the
 * day it comes back to the FTC queue), or something only the office can do
 * (Resubmit to Carrier, Correspondence sent, Details Needed by Smilepass — the note says what, and is
 * required). The options are the request type's own (outcomes.js), none is
 * preselected, and each says what saving it does: an answer or a hand-back
 * emails the office and moves the request to the top of their Ready tab, and
 * the last open item's answer completes the request.
 *
 * An entry recorded before outcomes had names ("done" / "update") keeps its
 * outcome unless one is picked — the server only judges an outcome that changes.
 *
 * Controlled inputs rather than antd Form, like PolicyEstimates; errors are
 * the server's, through describeApiError.
 */
export default function SubmissionResultForm({
  open,
  onClose,
  estimate,
  requestType,
  carrierName,
  patientId,
  initial,
  lastOpenInRequest,
  onSaved,
}) {
  const meta = requestTypeMeta(requestType);
  const isClaim = requestType === "claim_followup";
  const editing = !!(initial && initial.id);
  const doneLabel = isClaim ? "claim follow-up" : meta.submissionNoun;

  const [contactedOn, setContactedOn] = useState(dayjs());
  const [procedures, setProcedures] = useState([]);
  const [claimAmount, setClaimAmount] = useState(null);
  const [eligibleAmount, setEligibleAmount] = useState(null);
  const [payableAmount, setPayableAmount] = useState(null);
  const [paidAmount, setPaidAmount] = useState(null);
  const [note, setNote] = useState("");
  const choices = FTC_OUTCOMES_BY_TYPE[requestType] || FTC_OUTCOMES_BY_TYPE.estimate;
  const [outcome, setOutcome] = useState(null);
  const [followUpOn, setFollowUpOn] = useState(dayjs().add(7, "day"));
  const [fileList, setFileList] = useState([]);
  const [removeDocument, setRemoveDocument] = useState(false);
  const [saving, setSaving] = useState(false);

  // Seed the fields on open: from the result being edited, else from the
  // submission (its procedures are what the carrier was asked about).
  useEffect(
    function () {
      if (!open) return;
      const src = initial || {};
      setContactedOn(src.contacted_on ? dayjs(src.contacted_on) : dayjs());
      setProcedures(editing ? src.procedures || [] : (estimate && estimate.procedures) || []);
      setClaimAmount(toNumber(src.claim_amount));
      setEligibleAmount(toNumber(src.eligible_amount));
      setPayableAmount(toNumber(src.payable_amount));
      setPaidAmount(toNumber(src.paid_amount));
      setNote(src.note || "");
      // Nothing preselected for a new entry: the answer is the FTC's to pick.
      setOutcome(editing && choices.indexOf(src.outcome) !== -1 ? src.outcome : null);
      setFollowUpOn(src.follow_up_on ? dayjs(src.follow_up_on) : dayjs().add(7, "day"));
      setFileList([]);
      setRemoveDocument(false);
    },
    [open, editing, initial && initial.id, estimate && estimate.id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  async function save() {
    const values = {
      procedures: procedures,
      claim_amount: toField(claimAmount),
      eligible_amount: toField(eligibleAmount),
      payable_amount: toField(payableAmount),
      note: note,
    };
    if (outcome) values.outcome = outcome;
    if (contactedOn) values.contacted_on = contactedOn.format("YYYY-MM-DD");
    if (isClaim) values.paid_amount = toField(paidAmount);
    // The server enforces all of these too; saying it here saves a round trip.
    if (!editing && !outcome) {
      message.error("Choose what came back — the carrier's answer or what happens next.");
      return;
    }
    if (isOfficeOutcome(outcome) && !note.trim()) {
      message.error("Say what the office needs to do — it goes in the note.");
      return;
    }
    if (isFollowUp(outcome)) {
      if (!followUpOn) {
        message.error("Pick the day to check again.");
        return;
      }
      values.follow_up_on = followUpOn.format("YYYY-MM-DD");
    }
    const file = fileList.length && fileList[0].originFileObj ? fileList[0].originFileObj : null;

    setSaving(true);
    try {
      const res = editing
        ? await updateSubmissionResult(initial.id, values, file, { removeDocument: removeDocument })
        : await createSubmissionResult(estimate.id, values, file);
      if (res && res.status >= 200 && res.status < 300 && res.json) {
        onSaved(res.json);
        return;
      }
      message.error(describeApiError(res, "Couldn't save the result."));
    } finally {
      setSaving(false);
    }
  }

  const currentDocument = editing && initial.has_document && !removeDocument ? initial.document_name : null;
  const title = `${carrierName || "Carrier"} contacted on ${contactedOn ? contactedOn.format("MMM D, YYYY") : "…"}`;

  return (
    <Modal
      open={open}
      title={title}
      okText={editing ? "Save changes" : "Save result"}
      onOk={save}
      onCancel={onClose}
      confirmLoading={saving}
      destroyOnClose
      width={680}
    >
      <div className="est-result-form">
        <label className="est-field">
          <span className="est-field-label">Contacted on</span>
          <DatePicker
            value={contactedOn}
            onChange={setContactedOn}
            format="YYYY-MM-DD"
            allowClear={false}
            style={{ width: "100%" }}
          />
        </label>

        {/* The same picker the office used to name the procedures on the
            request, so the two lists read the same. */}
        <EstimateProcedures patientId={patientId} value={procedures} onChange={setProcedures} />

        <div className="est-result-form-grid">
          <label className="est-field">
            <span className="est-field-label">Claim amount</span>
            <InputNumber
              value={claimAmount}
              onChange={setClaimAmount}
              prefix="$"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </label>
          <label className="est-field">
            <span className="est-field-label">Eligible amount</span>
            <InputNumber
              value={eligibleAmount}
              onChange={setEligibleAmount}
              prefix="$"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </label>
          <label className="est-field">
            <span className="est-field-label">Payable amount</span>
            <InputNumber
              value={payableAmount}
              onChange={setPayableAmount}
              prefix="$"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </label>
          {isClaim ? (
            <label className="est-field">
              <span className="est-field-label">Paid amount</span>
              <InputNumber
                value={paidAmount}
                onChange={setPaidAmount}
                prefix="$"
                min={0}
                precision={2}
                style={{ width: "100%" }}
                placeholder="0.00"
              />
            </label>
          ) : null}
        </div>

        <label className="est-field">
          <span className="est-field-label">Note</span>
          <Input.TextArea
            value={note}
            onChange={function (e) { setNote(e.target.value); }}
            autoSize={{ minRows: 3, maxRows: 8 }}
            maxLength={4000}
            placeholder="Anything the office should know — what the carrier said, what is still outstanding…"
          />
        </label>

        <div className="est-field">
          <span className="est-field-label">Carrier's response (optional)</span>
          <div className="est-result-form-attachment">
            {currentDocument ? (
              <>
                <span><PaperClipOutlined /> {currentDocument}</span>
                <Button size="small" type="link" onClick={function () { setRemoveDocument(true); setFileList([]); }}>
                  Remove
                </Button>
              </>
            ) : null}
            {editing && initial.has_document && removeDocument ? (
              <>
                <span style={{ color: "#848696" }}>Attachment will be removed.</span>
                <Button size="small" type="link" onClick={function () { setRemoveDocument(false); }}>Undo</Button>
              </>
            ) : null}
            <Upload
              accept={ACCEPT}
              maxCount={1}
              fileList={fileList}
              beforeUpload={function () { return false; }}
              onChange={function (info) {
                setFileList(info.fileList.slice(-1));
                // A new file replaces the old one; asking to remove it as well
                // is what the server refuses.
                if (info.fileList.length) setRemoveDocument(false);
              }}
            >
              <Button size="small" icon={<UploadOutlined />}>
                {currentDocument ? "Replace" : "Attach a PDF or photo"}
              </Button>
            </Upload>
          </div>
        </div>

        <div className="est-field">
          <span className="est-field-label">What came back?</span>
          {editing && !outcome && initial.outcome ? (
            <div className="est-outcome-hint est-outcome-legacy">
              Recorded before outcomes had names ({outcomeLabel(initial.outcome)}). Pick one to set it, or save as
              it is.
            </div>
          ) : null}
          <Radio.Group
            value={outcome}
            onChange={function (e) { setOutcome(e.target.value); }}
            className="est-outcomes"
          >
            {choices.map(function (key) {
              const chosen = outcome === key;
              return (
                <div className="est-outcome" key={key}>
                  <div className="est-outcome-row">
                    <Radio value={key}>
                      <span className="est-outcome-dot" style={outcomeChipStyle(key)} aria-hidden="true" />
                      {outcomeLabel(key)}
                    </Radio>
                    {isFollowUp(key) ? (
                      <DatePicker
                        size="small"
                        value={followUpOn}
                        onChange={setFollowUpOn}
                        format="YYYY-MM-DD"
                        allowClear={false}
                        disabled={!chosen}
                        disabledDate={function (d) { return d && d.isBefore(dayjs().startOf("day")); }}
                        aria-label={"Day to check again — " + outcomeLabel(key)}
                      />
                    ) : null}
                  </div>
                  {chosen ? <OutcomeHint outcome={key} lastOpen={lastOpenInRequest} noun={doneLabel} /> : null}
                </div>
              );
            })}
          </Radio.Group>
        </div>
      </div>
    </Modal>
  );
}

// What saving this outcome does, said where the FTC is choosing it.
function OutcomeHint({ outcome, lastOpen, noun }) {
  if (isAnswer(outcome)) {
    return lastOpen ? (
      <div className="est-outcome-hint est-outcome-hint--warn">
        This is the last open {noun} on this request — saving completes it. The office is emailed and it moves to
        the top of their Ready tab.
      </div>
    ) : (
      <div className="est-outcome-hint">
        The office is emailed and the request moves to the top of their Ready tab.
      </div>
    );
  }
  if (isFollowUp(outcome)) {
    return (
      <div className="est-outcome-hint">
        Leaves your queue and comes back on the day you pick. The office sees it at the top of their Ready
        tab, with the date &mdash; no email.
      </div>
    );
  }
  if (isOfficeOutcome(outcome)) {
    return (
      <div className="est-outcome-hint">
        Say what the office needs in the note — it&rsquo;s what they read. The request goes to the top of their Ready
        tab, they&rsquo;re emailed, and it comes back to you when they resubmit.
      </div>
    );
  }
  return null;
}
